// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { DigitalTwinsClient } from "@azure/digital-twins-core";
import { DefaultHttpClient } from "@azure/core-http";
import { BatchService } from "./BatchService";
import { configService } from "./ConfigService";
import { REL_TYPE_ALL, REL_TYPE_INCOMING, REL_TYPE_OUTGOING, API_VERSION } from "./Constants";
import { print } from "./LoggingService";
import { settingsService } from "./SettingsService";
import { eventService } from "./EventService";
import { InteractiveBrowserCredential } from "@azure/identity";

import { authService } from "./AuthService";

// --- AUTHENTICATION CREDENTIAL ---
class AuthTokenCredential {
  async getToken(scopes) {
    const token = authService.getToken();
    const tokenParsed = authService.getTokenParsed();
    
    if (!token) {
      throw new Error("No authentication token available");
    }

    // Constants for token expiration logic
    const FALLBACK_EXPIRATION_MS = 180000; // 3 minutes
    const EXPIRATION_BUFFER_MS = 300000; // 5 minutes

    // Default fallback expiration if parsed token is missing
    let expiresOnTimestamp = Date.now() + FALLBACK_EXPIRATION_MS; 

    // Take expiration time from token itself and subtract the buffer
    if (tokenParsed && tokenParsed.exp) {
      expiresOnTimestamp = (tokenParsed.exp * 1000) - EXPIRATION_BUFFER_MS;
    }

    return {
      token: token,
      expiresOnTimestamp: expiresOnTimestamp
    };
  }
}

// --- HTTP CLIENT HACK ---
// This client tricks the SDK. It lets us pass "https" to the SDK so it doesn't complain,
// but then effectively downgrades the call to "http" so it actually connects to localhost.
class InsecureHttpClient {
  constructor() {
    this.client = new DefaultHttpClient();
  }

  async sendRequest(httpRequest) {
    // If the SDK is trying to call https://localhost, switch it back to http
    if (httpRequest.url.startsWith("https://localhost") || httpRequest.url.startsWith("https://127.0.0.1")) {
      httpRequest.url = httpRequest.url.replace("https://", "http://");
    }
    return this.client.sendRequest(httpRequest);
  }
}

const getAllTwinsQuery = "SELECT * FROM digitaltwins";

const getDataFromQueryResponse = response => {
  const list = [...response];
  const data = { twins: [], relationships: [], other: [] };
  for (let i = 0; i < list.length; i++) {
    const current = list[i];
    if (current.$dtId && current.$metadata && current.$metadata.$model && !data.twins.some(t => t.$dtId === current.$dtId)) {
      data.twins.push(current);
      continue;
    } else if (current.$relationshipId) {
      data.relationships.push(current);
      continue;
    }
    for (const k of Object.keys(current)) {
      const v = current[k];
      if (typeof v === "object") { list.push(v); }
      else if (Array.isArray(v)) { v.forEach(x => list.push(x)); }
      else { data.other.push(v); }
    }
  }
  return data;
};

class ApiService {

  constructor() {
    this.client = null;
  }

  async initialize() {
    const { appAdtUrl } = await configService.getConfig();

    // --- THE HACK ---
    const isLocal = appAdtUrl.includes("localhost") || appAdtUrl.includes("127.0.0.1");

    const isContainerApp = appAdtUrl.includes("azurecontainerapps.io") || appAdtUrl.includes("piilink.net");

    if (isLocal) {
      console.log("Using Insecure Mock Client for Localhost");

      // TRICK: We replace http with https so the SDK validator is happy.
      // The InsecureHttpClient below will swap it back to http before sending.
      const secureLookingUrl = appAdtUrl.replace("http://", "https://");

      this.client = new DigitalTwinsClient(
        secureLookingUrl,
        new AuthTokenCredential(),
        { httpClient: new InsecureHttpClient() }
      );

    } else if (isContainerApp) {
      console.log("Using Authentication Credential for Azure Container Apps proxy");
      this.client = new DigitalTwinsClient(
        appAdtUrl,
        new AuthTokenCredential(),
        { httpClient: new DefaultHttpClient() }
      );
    } else {
      // Original logic for real Azure connection
      const httpClient = new DefaultHttpClient();
      this.client = new DigitalTwinsClient(
        appAdtUrl,
        new InteractiveBrowserCredential({ tenantId: "common", clientId: "common" }),
        { httpClient }
      );
    }
  }

  async query(query, callback) {
    await this.initialize();
    let count = 1;
    for await (const page of this.client.queryTwins(query).byPage()) {
      print(`Ran query for twins, page ${count++}:`, "info");
      print(JSON.stringify(page, null, 2), "info");
      await callback(getDataFromQueryResponse(page.value));
    }
  }

  async queryTwins(query) {
    const list = [];
    await this.query(query, ({ twins }) => twins.forEach(x => list.push(x)));
    return list;
  }

  async getAllTwins() { return await this.queryTwins(getAllTwinsQuery); }

  async getTwinById(twinId) {
    await this.initialize();
    const response = await this.client.getDigitalTwin(twinId);
    return response.body;
  }

  async addTwin(twinId, payload) {
    await this.initialize();
    return await this.client.upsertDigitalTwin(twinId, JSON.stringify(payload));
  }

  async batchUploadTwins(twins) {
    const { appAdtUrl } = await configService.getConfig();
    const response = await fetch(`${appAdtUrl.replace(/\/$/, "")}/digitaltwins/twinsupload`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(twins)
    });

    if (!response.ok) {
      console.log("Error in batch upload", response);
      return null;
    }
    return response.json();
  }

  async uploadRelationships(relationships) {
    const { appAdtUrl } = await configService.getConfig();
    const response = await fetch(`${appAdtUrl.replace(/\/$/, "")}/digitaltwins/relations/upload`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(relationships)
    });

    if (!response.ok) {
      console.error("ApiService: Error in batch upload relationships", response.status, response.statusText);
      return null;
    }
    return response.json();
  }

  async updateTwin(twinId, patch) {
    await this.initialize();
    return await this.client.updateDigitalTwin(twinId, patch);
  }

  async updateRelationship(twinId, relationshipId, patch) {
    await this.initialize();
    return await this.client.updateRelationship(twinId, relationshipId, patch);
  }

  async queryRelationshipsPaged(twinIds, callback, type = REL_TYPE_OUTGOING) {
    await this.initialize();
    const operations = type === REL_TYPE_ALL ? [REL_TYPE_OUTGOING, REL_TYPE_INCOMING] : [type];
    for (let i = 0; i < operations.length; i++) {
      const op = operations[i];
      const isFinalOp = i === operations.length - 1;
      const basePropertyName = op === REL_TYPE_INCOMING ? "$targetId" : "$sourceId";
      let count = 1;
      const twinIdsList = twinIds.map(twinId => `'${twinId.replaceAll("'", "\\'")}'`);
      const query = `SELECT * FROM RELATIONSHIPS r WHERE r.${basePropertyName} IN [${twinIdsList.join(",")}]`;
      for await (const page of this.client.queryTwins(query).byPage()) {
        const callbackResponse = [...page.value];
        if (page.continuationToken || !isFinalOp) { callbackResponse.nextLink = true; }
        await callback(callbackResponse);
      }
    }
  }

  async queryRelationships(twinIds, type = REL_TYPE_OUTGOING) {
    const list = [];
    await this.queryRelationshipsPaged(twinIds, items => items.forEach(x => list.push(x)), type);
    return list;
  }

  async addRelationship(sourceId, targetId, relationshipType, relationshipId, properties) {
    await this.initialize();
    return await this.client.upsertRelationship(sourceId, relationshipId,
      { ...properties, $relationshipName: relationshipType, $targetId: targetId });
  }

  async queryModels() {
    await this.initialize();
    const list = [];
    const models = this.client.listModels([], true);
    for await (const model of models) { list.push(model); }
    return list;
  }

  async getModelById(modelId) {
    await this.initialize();
    return await this.client.getModel(modelId, true);
  }

  async addModels(models) {
    await this.initialize();

    try {
      return await this.client.createModels(models);
    } catch (exc) {

      const parsedBody = exc?.response?.parsedBody;
      const bodyAsText = exc?.response?.bodyAsText;

      const errorCode = parsedBody?.error?.code;

      // ❗ Allow real ADT errors to propagate normally
      if (errorCode && errorCode !== "InternalServerError") {
        throw exc;
      }

      // Only ignore the fake local mock error
      const looksLikeSuccess =
        Array.isArray(parsedBody) ||
        (typeof bodyAsText === "string" && bodyAsText.includes('"uploadTime"'));

      if (exc?.name === "RestError" && looksLikeSuccess) {
        print("*** Upload succeeded (local mock workaround)", "warning");

        if (Array.isArray(parsedBody)) {
          return parsedBody;
        }

        try {
          return JSON.parse(bodyAsText);
        } catch {
          return true;
        }
      }

      // All other errors behave normally
      throw exc;
    }
  }

  async deleteRelationship(twinId, relationshipId) {
    await this.initialize();
    await this.client.deleteRelationship(twinId, relationshipId);
  }

  async deleteTwin(twinId, skipRelationships = false) {
    if (!skipRelationships) { await this.deleteTwinRelationships(twinId); }
    await this.client.deleteDigitalTwin(twinId);
  }

  async deleteTwinRelationships(twinId, skipIncoming = false) {
    await this.initialize();
    const rels = await this.queryRelationships([twinId], REL_TYPE_OUTGOING);
    for (const r of rels) { await this.deleteRelationship(twinId, r.$relationshipId); }
    if (!skipIncoming) {
      const incRels = await this.queryRelationships([twinId], REL_TYPE_INCOMING);
      for (const r of incRels) { await this.deleteRelationship(r.$sourceId, r.$relationshipId); }
    }
  }

  async deleteAllTwins(ids) {
    await this.initialize();
    const relsBs = new BatchService({
      items: ids,
      action: (item, resolve, reject) => { this.deleteTwinRelationships(item, true).then(resolve, reject); }
    });
    await relsBs.run();
    const twinsBs = new BatchService({
      items: ids,
      action: (item, resolve, reject) => { this.deleteTwin(item, true).then(resolve, reject); }
    });
    await twinsBs.run();
    print("*** Delete complete", "warning");
  }

  async deleteModel(id) {
    await this.initialize();
    await this.client.deleteModel(id);
  }

  async getRelationship(sourceTwinId, relationshipId) {
    await this.initialize();
    return await this.client.getRelationship(sourceTwinId, relationshipId);
  }

  async getEventRoutes() {
    await this.initialize();
    const list = [];
    const eventRoutes = this.client.listEventRoutes();
    for await (const eventRoute of eventRoutes) { list.push(eventRoute); }
    return list;
  }

  async getEventRoute(routeId) {
    await this.initialize();
    return await this.client.getEventRoute(routeId);
  }

  async addEventRoute(routeId, endpointId, filter) {
    await this.initialize();
    const eventRoute = { id: routeId, endpointId, filter };
    return await this.client.upsertEventRoute(routeId, { options: eventRoute });
  }

  async deleteEventRoute(routeId) {
    await this.initialize();
    await this.client.deleteEventRoute(routeId);
  }

  async decommissionModel(modelId) {
    await this.initialize();
    await this.client.decomissionModel(modelId);
  }
}

class CachedApiService extends ApiService {
  constructor() {
    super();
    this.cache = { relationships: {}, models: [] };
    eventService.subscribeClearCache(() => { this.clearCache(); });
  }
  async addModels(models) { this.cache.models = []; return await super.addModels(models); }
  async uploadRelationships(relationships) {
    const result = await super.uploadRelationships(relationships);
    if (result) {
      this.cache.relationships = {};
    }
    return result;
  }
  async deleteModel(id) { this.cache.models = []; return await super.deleteModel(id); }
  async getModelById(id) {
    if (!settingsService.caching) { this.clearCache(); return await super.getModelById(id); }
    if (this.cache.models.length <= 0) { await this.updateModelCache(); }
    return this.cache.models.find(m => m.id === id);
  }
  async queryModels(bypassCache = false) {
    if (!settingsService.caching || bypassCache) { this.clearCache(); return await super.queryModels(); }
    if (this.cache.models.length <= 0) { await this.updateModelCache(); }
    return this.cache.models;
  }
  async deleteAllTwins(ids) { this.cache.relationships = {}; return await super.deleteAllTwins(ids); }
  async updateModelCache() { const models = await super.queryModels(); this.cache.models = models; }
  async addRelationship(sourceId, targetId, relationshipType, relationshipId, properties) {
    for (const id of [sourceId, targetId]) { if (this.cache.relationships[id]) { delete this.cache.relationships[id]; } }
    return await super.addRelationship(sourceId, targetId, relationshipType, relationshipId, properties);
  }
  async deleteRelationship(twinId, relationshipId) {
    if (this.cache.relationships[twinId]) { delete this.cache.relationships[twinId]; }
    return await super.deleteRelationship(twinId, relationshipId);
  }
  async queryRelationshipsPaged(twinIds, callback, type = REL_TYPE_OUTGOING) {
    if (!settingsService.caching) { this.clearCache(); await super.queryRelationshipsPaged(twinIds, callback, type); return; }
    const pendingTwins = [];
    let relationshipsResult = [];
    twinIds.forEach(twinId => {
      const results = this.cache.relationships[twinId];
      if (results && results[type]) { relationshipsResult = relationshipsResult.concat(results[type]); }
      else { pendingTwins.push(twinId); }
      if (!results) { this.cache.relationships[twinId] = {}; }
    });
    if (pendingTwins.length > 0) {
      await super.queryRelationshipsPaged(pendingTwins, items => {
        items.forEach(x => relationshipsResult.push(x));
        pendingTwins.forEach(twinId => { this.cache.relationships[twinId][type] = items.filter(item => item.$sourceId === twinId); });
      }, type);
    }
    await callback(relationshipsResult);
  }
  clearCache() { this.cache.relationships = {}; this.cache.models = []; }
}

export const apiService = new CachedApiService();
